import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import Layout from '../components/Layout';
import { useAuth } from '../context/AuthContext';
import { useMerchant } from '../context/MerchantContext';
import { customerApi, orderApi } from '../services/api';
import { PageWalletTransactionDto, WalletBalanceDto, WalletTransactionDto } from '../types';
import format from 'date-fns/format';
import ptBR from 'date-fns/locale/pt-BR';
import toast from 'react-hot-toast';

const PageContainer = styled.div`
  max-width: 1200px;
  margin: 2rem auto;
  padding: 0 1rem;
`;

const ContentGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 350px;
  gap: 2rem;
  align-items: start;

  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
  }
`;

const MainContent = styled.div`
  display: flex;
  flex-direction: column;
`;

const Sidebar = styled.aside`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const PromoCard = styled.div`
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border-radius: 16px;
  padding: 1.5rem;
  color: white;
  position: relative;
  overflow: hidden;
  box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1);

  h3 {
    font-size: 1.25rem;
    margin: 0 0 0.5rem 0;
    font-weight: 700;
  }

  p {
    font-size: 0.9rem;
    margin: 0;
    opacity: 0.9;
    line-height: 1.5;
  }

  .icon-bg {
    position: absolute;
    right: -10px;
    bottom: -10px;
    font-size: 5rem;
    opacity: 0.2;
    transform: rotate(-15deg);
  }
`;

const Title = styled.h1`
  font-size: 2rem;
  color: #2d3748;
  margin-bottom: 1rem;
`;

const CashbackCard = styled.div`
  background: linear-gradient(135deg, #2ecc71 0%, #27ae60 100%);
  border-radius: 16px;
  padding: 2rem;
  color: white;
  margin-bottom: 2rem;
  box-shadow: 0 10px 20px rgba(46, 204, 113, 0.2);
`;

const BalanceGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1.5rem;
`;

const BalanceItem = styled.div`
  display: flex;
  flex-direction: column;
`;

const BalanceLabel = styled.div`
  font-size: 0.875rem;
  opacity: 0.9;
  margin-bottom: 0.25rem;
`;

const BalanceValue = styled.div<{ size?: string }>`
  font-size: ${props => props.size || '2rem'};
  font-weight: 800;
`;

const Section = styled.section`
  margin-bottom: 2rem;
`;

const SectionTitle = styled.h2`
  font-size: 1.25rem;
  color: #2d3748;
  margin-bottom: 1rem;
`;

const InfoCard = styled.div`
  background: white;
  padding: 1.5rem;
  border-radius: 12px;
  border: 1px solid #e2e8f0;
  margin-bottom: 1rem;
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const StepItem = styled.div`
  display: flex;
  align-items: flex-start;
  text-align: left;
  gap: 1rem;

  .icon {
    width: 32px;
    height: 32px;
    background: #f0fff4;
    color: #27ae60;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    font-weight: bold;
    font-size: 0.875rem;
  }

  .content {
    h3 {
      margin: 0 0 0.25rem 0;
      font-size: 0.95rem;
      color: #2d3748;
    }
    p {
      margin: 0;
      color: #718096;
      font-size: 0.8rem;
      line-height: 1.4;
    }
  }
`;

const TransactionList = styled.div`
  background: white;
  border-radius: 12px;
  border: 1px solid #e2e8f0;
  overflow: hidden;
`;

const TransactionItem = styled.div`
  padding: 1rem 1.5rem;
  border-bottom: 1px solid #e2e8f0;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 1rem;

  &:last-child {
    border-bottom: none;
  }

  .transaction-main {
    display: flex;
    gap: 1rem;
    align-items: center;
    flex: 1;
    min-width: 0;
  }

  .info {
    flex: 1;
    min-width: 0;
    padding-right: 1rem;

    .type {
      font-weight: 600;
      color: #2d3748;
      font-size: 0.95rem;
      margin-bottom: 0.25rem;
    }
    .date {
      font-size: 0.8rem;
      color: #718096;
    }
    .description {
      font-size: 0.85rem;
      color: #4a5568;
      margin-top: 0.25rem;
      line-height: 1.45;

      strong {
        color: #2d3748;
        font-weight: 700;
      }
    }
  }

  .amount {
    font-weight: 700;
    font-size: 1rem;
    white-space: nowrap;
    
    &.positive {
      color: #27ae60;
    }
    &.negative {
      color: #e53e3e;
    }
    &.pending {
      color: #a0aec0;
    }
  }

  .transaction-side {
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    gap: 0.45rem;
    flex-shrink: 0;
  }

  @media (max-width: 560px) {
    align-items: flex-start;
    flex-direction: column;

    .transaction-side {
      width: 100%;
      align-items: flex-start;
      padding-left: 46px;
    }
  }
`;

const EmptyState = styled.div`
  padding: 3rem;
  text-align: center;
  color: #a0aec0;
  font-size: 0.9rem;
`;

const PaginationBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  margin-top: 1rem;
  color: #4a5568;
  font-size: 0.9rem;

  @media (max-width: 480px) {
    align-items: stretch;
    flex-direction: column;
  }
`;

const PaginationActions = styled.div`
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
  justify-content: flex-end;
`;

const PaginationButton = styled.button<{ $active?: boolean; $themeColor: string }>`
  min-width: 2.25rem;
  border: 1px solid ${props => props.$active ? props.$themeColor : '#e2e8f0'};
  border-radius: 8px;
  background: ${props => props.$active ? props.$themeColor : 'white'};
  color: ${props => props.$active ? 'white' : '#2d3748'};
  font-weight: 600;
  padding: 0.6rem 0.9rem;
  cursor: pointer;

  &:disabled {
    color: ${props => props.$active ? 'white' : '#a0aec0'};
    cursor: not-allowed;
    background: ${props => props.$active ? props.$themeColor : '#f7fafc'};
  }

  &:not(:disabled):hover {
    border-color: ${props => props.$themeColor};
    color: ${props => props.$active ? 'white' : props.$themeColor};
  }
`;

const OrderButton = styled.button<{ $themeColor: string }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.4rem;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  background: #f8fafc;
  color: #4a5568;
  font-size: 0.75rem;
  font-weight: 700;
  line-height: 1;
  padding: 0.45rem 0.7rem;
  white-space: nowrap;
  cursor: pointer;
  transition: all 0.2s ease;

  &:not(:disabled):hover {
    background: #fff;
    border-color: ${props => props.$themeColor};
    color: ${props => props.$themeColor};
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.06);
  }

  &:disabled {
    color: #a0aec0;
    cursor: wait;
    background: #f7fafc;
    border-color: #e2e8f0;
  }
`;

const SkeletonBlock = styled.div<{ width?: string; height?: string }>`
  width: ${props => props.width || '100%'};
  height: ${props => props.height || '1rem'};
  border-radius: 6px;
  background: linear-gradient(90deg, #edf2f7 25%, #f8fafc 37%, #edf2f7 63%);
  background-size: 400% 100%;
  animation: skeleton-loading 1.4s ease infinite;

  @keyframes skeleton-loading {
    0% { background-position: 100% 50%; }
    100% { background-position: 0 50%; }
  }
`;

const TRANSACTIONS_PAGE_SIZE = 20;

const CashbackPage: React.FC = () => {
  const { isAuthenticated, user, updateUser } = useAuth();
  const { restaurant, setIsCheckoutModalOpen, setSelectedOrder } = useMerchant();
  const [balance, setBalance] = useState<WalletBalanceDto | null>(null);
  const [transactions, setTransactions] = useState<WalletTransactionDto[]>([]);
  const [transactionsPage, setTransactionsPage] = useState<PageWalletTransactionDto | null>(null);
  const [currentTransactionsPage, setCurrentTransactionsPage] = useState(0);
  const [loading, setLoading] = useState(false);
  const [loadingTransactionKey, setLoadingTransactionKey] = useState<string | null>(null);

  useEffect(() => {
    if (isAuthenticated && restaurant?.id) {
      loadWalletData();
    }
  }, [isAuthenticated, restaurant?.id, currentTransactionsPage]);

  useEffect(() => {
    setCurrentTransactionsPage(0);
  }, [restaurant?.id]);

  const loadWalletData = async () => {
    if (!restaurant?.id) return;
    
    setLoading(true);
    try {
      const [balanceRes, transactionsRes] = await Promise.all([
        customerApi.getWalletBalance(restaurant.id),
        customerApi.getWalletTransactions(restaurant.id, currentTransactionsPage, TRANSACTIONS_PAGE_SIZE)
      ]);
      setBalance(balanceRes);
      setTransactions(transactionsRes.content);
      setTransactionsPage(transactionsRes);
      
      // Update user context with latest balance
      updateUser({
        availableCashbackBalance: balanceRes.availableBalance,
        pendingCashbackBalance: balanceRes.pendingCashback
      });
    } catch (error) {
      console.error('Error loading wallet data:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);
  };

  const formatOrderReference = (orderExternalId?: string) => {
    if (!orderExternalId) return '';
    return `#${orderExternalId}`;
  };

  const getTransactionDescription = (tx: WalletTransactionDto) => {
    if (tx.description) return tx.description;

    const orderReference = formatOrderReference(tx.orderExternalId);
    const orderSuffix = orderReference ? ` do pedido ${orderReference}` : '';

    switch (tx.type) {
      case 'PAYMENT':
        return `Pagamento${orderSuffix}`;
      case 'CASHBACK_EARN':
        return `Cashback pendente${orderSuffix}`;
      case 'CASHBACK_RELEASED':
        return `Cashback liberado${orderSuffix}`;
      case 'CASHBACK_USE':
        return `Cashback utilizado${orderSuffix}`;
      case 'CASHBACK_REVERSAL':
        return `Estorno de cashback${orderSuffix}`;
      case 'CASHBACK_EXPIRED':
        return `Cashback expirado${orderSuffix}`;
      default:
        return '';
    }
  };

  const getTransactionOrderId = (tx: WalletTransactionDto) => {
    return tx.orderId || tx.orderExternalId || '';
  };

  const handleOpenOrderDetails = async (tx: WalletTransactionDto, transactionKey: string) => {
    const orderId = getTransactionOrderId(tx);
    if (!restaurant?.id || !orderId) return;

    setLoadingTransactionKey(transactionKey);
    try {
      const order = await orderApi.getOrder(restaurant.id, orderId);
      setSelectedOrder(order);
      setIsCheckoutModalOpen(true);
    } catch (error) {
      console.error('Error loading order details from wallet transaction:', error);
      toast.error('Não foi possível carregar os dados do pedido.');
    } finally {
      setLoadingTransactionKey(null);
    }
  };

  const sanitizeHtmlDescription = (description: string) => {
    return description
      .replace(/<script[\s\S]*?>[\s\S]*?<\/script>/gi, '')
      .replace(/\son\w+=(?:"[^"]*"|'[^']*'|[^\s>]+)/gi, '')
      .replace(/\s(href|src)=(?:"javascript:[^"]*"|'javascript:[^']*'|javascript:[^\s>]*)/gi, '');
  };

  const getTransactionLabel = (type: string) => {
    switch (type) {
      case 'CASHBACK_EARN': return 'Cashback agendado';
      case 'CASHBACK_USE': return 'Cashback utilizado';
      case 'CASHBACK_REVERSAL': return 'Estorno de cashback';
      case 'CASHBACK_EXPIRED': return 'Cashback expirado';
      case 'CASHBACK_RELEASED': return 'Cashback disponibilizado';
      case 'PAYMENT': return 'Pagamento do pedido';
      default: return type;
    }
  };

  const isPositive = (type: string) => {
    return type === 'CASHBACK_RELEASED' || type === 'CASHBACK_REVERSAL';
  };

  const getTransactionStatus = (tx: WalletTransactionDto) => {
    if (tx.direction === 'IN') return 'positive';
    if (tx.direction === 'OUT') return 'negative';
    if (tx.type === 'CASHBACK_EARN') return 'pending';
    const type = tx.type;
    if (isPositive(type)) return 'positive';
    return 'negative';
  };

  const getTransactionIcon = (type: string) => {
    switch (type) {
      case 'CASHBACK_EARN': return <i className="fas fa-clock" style={{ color: '#a0aec0', fontSize: '1.2rem' }}></i>;
      case 'CASHBACK_RELEASED': return <i className="fas fa-arrow-up" style={{ color: '#27ae60', fontSize: '1.2rem' }}></i>;
      case 'CASHBACK_USE': return <i className="fas fa-arrow-down" style={{ color: '#e53e3e', fontSize: '1.2rem' }}></i>;
      case 'CASHBACK_REVERSAL': return <i className="fas fa-undo" style={{ color: '#27ae60', fontSize: '1.2rem' }}></i>;
      case 'CASHBACK_EXPIRED': return <i className="fas fa-history" style={{ color: '#e53e3e', fontSize: '1.2rem' }}></i>;
      case 'PAYMENT': return <i className="fas fa-exchange-alt" style={{ color: '#1a202c', fontSize: '1.2rem' }}></i>;
      default: return <i className="fas fa-exchange-alt" style={{ fontSize: '1.2rem' }}></i>;
    }
  };

  const availableBalance = balance?.availableBalance ?? user?.availableCashbackBalance ?? 0;
  const pendingBalance = balance?.pendingCashback ?? user?.pendingCashbackBalance ?? 0;
  const expiredBalance = user?.expiredCashbackBalance || 0;
  const showTransactionsPagination = !!transactionsPage && transactionsPage.totalPages > 1;
  const themeColor = restaurant?.theme?.primary || '#2d3748';
  const getVisiblePages = (currentPage: number, totalPages: number) => {
    const maxVisible = 5;
    const half = Math.floor(maxVisible / 2);
    let start = Math.max(0, currentPage - half);
    let end = Math.min(totalPages - 1, start + maxVisible - 1);

    start = Math.max(0, end - maxVisible + 1);

    return Array.from({ length: end - start + 1 }, (_, index) => start + index);
  };

  const renderTransactionSkeletons = () => (
    <TransactionList>
      {Array.from({ length: 5 }).map((_, index) => (
        <TransactionItem key={index}>
          <div style={{ display: 'flex', gap: '1rem', alignItems: 'center', flex: 1 }}>
            <SkeletonBlock width="30px" height="30px" />
            <div style={{ flex: 1 }}>
              <SkeletonBlock width="42%" height="1rem" />
              <div style={{ marginTop: '0.55rem' }}>
                <SkeletonBlock width="32%" height="0.8rem" />
              </div>
              <div style={{ marginTop: '0.55rem' }}>
                <SkeletonBlock width="58%" height="0.85rem" />
              </div>
            </div>
          </div>
          <SkeletonBlock width="90px" height="1rem" />
        </TransactionItem>
      ))}
    </TransactionList>
  );

  return (
    <Layout>
      <PageContainer>
        <Title>Minha Carteira</Title>
        <p style={{ color: '#718096', marginBottom: '2rem' }}>
          Gerencie seu saldo de cashback e acompanhe suas movimentações no {restaurant?.name}.
        </p>

        <ContentGrid>
          <MainContent>
            {isAuthenticated && restaurant?.cashbackConfig?.enabled && (
              <>
                <CashbackCard>
                  <BalanceGrid>
                    <BalanceItem>
                      <BalanceLabel>Saldo Disponível</BalanceLabel>
                      <BalanceValue>{formatCurrency(availableBalance)}</BalanceValue>
                    </BalanceItem>
                    <BalanceItem>
                      <BalanceLabel>Pendente</BalanceLabel>
                      <BalanceValue size="1.5rem">{formatCurrency(pendingBalance)}</BalanceValue>
                    </BalanceItem>
                  </BalanceGrid>
                </CashbackCard>

                {expiredBalance > 0 && (
                  <div style={{ background: '#fff5f5', color: '#c53030', padding: '1rem', borderRadius: '12px', marginBottom: '2rem', fontSize: '0.9rem' }}>
                    <i className="fas fa-exclamation-circle" style={{ marginRight: '0.5rem' }}></i>
                    Você possui <strong>{formatCurrency(expiredBalance)}</strong> em cashback expirado.
                  </div>
                )}

                <Section>
                  <SectionTitle>Extrato Financeiro</SectionTitle>
                  {loading ? (
                    renderTransactionSkeletons()
                  ) : transactions.length > 0 ? (
                    <>
                      <TransactionList>
                        {transactions.map((tx, index) => {
                          const description = getTransactionDescription(tx);
                          const transactionStatus = getTransactionStatus(tx);
                          const orderId = getTransactionOrderId(tx);
                          const transactionKey = `${orderId || 'no-order'}-${tx.createdAt}-${index}`;
                          const isOrderLoading = loadingTransactionKey === transactionKey;

                          return (
                            <TransactionItem key={`${tx.createdAt}-${index}`}>
                              <div className="transaction-main">
                                <div style={{ width: '30px', display: 'flex', justifyContent: 'center' }}>
                                  {getTransactionIcon(tx.type)}
                                </div>
                                <div className="info">
                                  <div className="type">{getTransactionLabel(tx.type)}</div>
                                  <div className="date">{format(new Date(tx.createdAt), "dd 'de' MMMM 'às' HH:mm", { locale: ptBR })}</div>
                                  {description && (
                                    <div
                                      className="description"
                                      dangerouslySetInnerHTML={{ __html: sanitizeHtmlDescription(description) }}
                                    />
                                  )}
                                </div>
                              </div>
                              <div className="transaction-side">
                                <div className={`amount ${transactionStatus}`}>
                                  {transactionStatus === 'positive' ? '+' : transactionStatus === 'negative' ? '-' : ''} {formatCurrency(Math.abs(tx.amount))}
                                </div>
                                {orderId && (
                                  <OrderButton
                                    type="button"
                                    $themeColor={themeColor}
                                    disabled={isOrderLoading}
                                    onClick={() => handleOpenOrderDetails(tx, transactionKey)}
                                  >
                                    <i className={isOrderLoading ? 'fas fa-spinner fa-spin' : 'fas fa-receipt'}></i>
                                    {isOrderLoading ? 'Abrindo' : 'Pedido'}
                                  </OrderButton>
                                )}
                              </div>
                            </TransactionItem>
                          );
                        })}
                      </TransactionList>

                      {showTransactionsPagination && (
                        <PaginationBar>
                          <span>
                            Página {(transactionsPage?.number ?? 0) + 1} de {transactionsPage?.totalPages}
                          </span>
                          <PaginationActions>
                            <PaginationButton
                              type="button"
                              $themeColor={themeColor}
                              disabled={loading || transactionsPage?.first}
                              onClick={() => setCurrentTransactionsPage(0)}
                            >
                              Primeira
                            </PaginationButton>
                            <PaginationButton
                              type="button"
                              $themeColor={themeColor}
                              disabled={loading || transactionsPage?.first}
                              onClick={() => setCurrentTransactionsPage(page => Math.max(0, page - 1))}
                            >
                              Anterior
                            </PaginationButton>
                            {getVisiblePages(transactionsPage?.number ?? 0, transactionsPage?.totalPages ?? 0).map(page => (
                              <PaginationButton
                                key={page}
                                type="button"
                                $themeColor={themeColor}
                                $active={page === transactionsPage?.number}
                                disabled={loading || page === transactionsPage?.number}
                                onClick={() => setCurrentTransactionsPage(page)}
                              >
                                {page + 1}
                              </PaginationButton>
                            ))}
                            <PaginationButton
                              type="button"
                              $themeColor={themeColor}
                              disabled={loading || transactionsPage?.last}
                              onClick={() => setCurrentTransactionsPage(page => page + 1)}
                            >
                              Próxima
                            </PaginationButton>
                            <PaginationButton
                              type="button"
                              $themeColor={themeColor}
                              disabled={loading || transactionsPage?.last}
                              onClick={() => setCurrentTransactionsPage(Math.max(0, (transactionsPage?.totalPages ?? 1) - 1))}
                            >
                              Última
                            </PaginationButton>
                          </PaginationActions>
                        </PaginationBar>
                      )}
                    </>
                  ) : (
                    <TransactionList>
                      <EmptyState>Nenhuma movimentação encontrada.</EmptyState>
                    </TransactionList>
                  )}
                </Section>
              </>
            )}

            {!restaurant?.cashbackConfig?.enabled && (
              <Section>
                <InfoCard style={{ textAlign: 'center', padding: '3rem' }}>
                  <i className="fas fa-info-circle" style={{ fontSize: '3rem', color: '#cbd5e0', marginBottom: '1rem' }}></i>
                  <h3 style={{ color: '#2d3748', marginBottom: '0.5rem' }}>Cashback não disponível</h3>
                  <p style={{ color: '#718096' }}>O programa de cashback não está ativado para este estabelecimento no momento.</p>
                </InfoCard>
              </Section>
            )}
          </MainContent>

          <Sidebar>
            <PromoCard>
              <i className="fas fa-gift icon-bg"></i>
              <h3>Economize mais!</h3>
              <p>Cada pedido que você faz te aproxima de uma refeição grátis. Aproveite nosso sistema de recompensas.</p>
            </PromoCard>

            <Section>
              <SectionTitle>Como funciona o cashback</SectionTitle>
              <InfoCard>
                <StepItem>
                  <div className="icon">1</div>
                  <div className="content">
                    <h3>Faça seu pedido</h3>
                    <p>Realize suas compras normalmente em nossa loja.</p>
                  </div>
                </StepItem>
                <StepItem>
                  <div className="icon">2</div>
                  <div className="content">
                    <h3>Ganhe Cashback</h3>
                    {restaurant?.cashbackConfig?.enabled ? (
                      <p>
                        Ganhe {restaurant.cashbackConfig.type === 'PERCENTAGE' ? `${restaurant.cashbackConfig.value}%` : formatCurrency(restaurant.cashbackConfig.value)} de volta.
                        {restaurant.cashbackConfig.minOrderValue > 0 && ` Válido para pedidos acima de ${formatCurrency(restaurant.cashbackConfig.minOrderValue)}.`}
                      </p>
                    ) : (
                      <p>Cada compra gera um valor de volta para você.</p>
                    )}
                  </div>
                </StepItem>
                <StepItem>
                  <div className="icon">3</div>
                  <div className="content">
                    <h3>Acompanhe o status</h3>
                    <p>O valor fica pendente até a conclusão do pedido e depois fica disponível para uso.</p>
                  </div>
                </StepItem>
                <StepItem>
                  <div className="icon">4</div>
                  <div className="content">
                    <h3>Use em novos pedidos</h3>
                    <p>No checkout, selecione a opção de usar seu saldo de cashback para pagar parte ou o total do seu pedido.</p>
                  </div>
                </StepItem>
              </InfoCard>
            </Section>

            {restaurant?.cashbackConfig?.expirationDays && (
              <Section>
                <SectionTitle>Regras de Expiração</SectionTitle>
                <InfoCard>
                  <p style={{ color: '#718096', margin: 0 }}>
                    Seu cashback tem validade de <strong>{restaurant.cashbackConfig.expirationDays} dias</strong> após ficar disponível.
                  </p>
                </InfoCard>
              </Section>
            )}
          </Sidebar>
        </ContentGrid>
      </PageContainer>
    </Layout>
  );
};

export default CashbackPage;
